import { isPlaceholderAiKey } from "./server-ai-runtime.js";
import { ProviderRateLimitRetryableError, parseRetryAfterHeaderMs } from "./provider-rate-limit-retry.js";

const DEFAULT_EMBEDDING_MODEL = "text-embedding-3-small";
const DEFAULT_EMBEDDING_DIMENSION = 1536;

export const RAG_EMBEDDING_BATCH_SIZE = 16;

export type RagEmbeddingTarget = {
  endpoint: string;
  token: string;
};

export function createRagEmbeddingBatches<T>(items: T[], size = RAG_EMBEDDING_BATCH_SIZE): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
}

function appendEmbeddingsPath(baseUrl: string): string {
  const normalized = baseUrl.replace(/\/+$/, "");
  return normalized.endsWith("/embeddings") ? normalized : `${normalized}/embeddings`;
}

function uniqueEmbeddingTargets(targets: RagEmbeddingTarget[]): RagEmbeddingTarget[] {
  const seen = new Set<string>();
  return targets.filter((target) => {
    const key = `${target.endpoint}\n${target.token}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function resolveRagEmbeddingTargets(env: NodeJS.ProcessEnv = process.env): RagEmbeddingTarget[] {
  const proxyToken = env.GENCOW_AI_PROXY_TOKEN?.trim();
  const proxyUrls = [env.GENCOW_AI_PROXY_URL, env.GENCOW_AI_PROXY_URL_ALT]
    .map((value) => value?.trim())
    .filter((value): value is string => !!value);
  const targets: RagEmbeddingTarget[] = [];
  const hasProxyConfig = proxyUrls.length > 0 || !!proxyToken;

  if (proxyToken) {
    for (const proxyUrl of proxyUrls) {
      targets.push({ endpoint: appendEmbeddingsPath(proxyUrl), token: proxyToken });
    }
  }

  const directKey = env.OPENAI_API_KEY?.trim();
  const allowDirectOpenAiFallback = env.GENCOW_RAG_ALLOW_DIRECT_OPENAI_FALLBACK === "1";
  if (!hasProxyConfig && allowDirectOpenAiFallback && directKey && !isPlaceholderAiKey(directKey)) {
    targets.push({ endpoint: "https://api.openai.com/v1/embeddings", token: directKey });
  }

  return uniqueEmbeddingTargets(targets);
}

function shouldRetryEmbeddingResponse(status: number): boolean {
  return status === 404 || status === 408 || status >= 500;
}

function extractEmbeddingError(json: unknown): string | null {
  if (!json || typeof json !== "object") return null;
  const error = (json as { error?: unknown }).error;
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }
  return null;
}

function extractEmbeddingErrorCode(json: unknown): string | null {
  if (!json || typeof json !== "object") return null;
  const error = (json as { error?: unknown }).error;
  if (!error || typeof error !== "object") return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && code.trim() ? code : null;
}

function validateEmbeddingList(json: unknown, inputCount: number): number[][] {
  if (!json || typeof json !== "object" || !Array.isArray((json as { data?: unknown }).data)) {
    throw new Error("AI embeddings endpoint returned JSON without data[]");
  }

  const data = (json as { data: unknown[] }).data;
  const embeddings: Array<number[] | undefined> = new Array(inputCount);
  let validCount = 0;

  data.forEach((item, fallbackIndex) => {
    if (!item || typeof item !== "object") return;
    const rawIndex = (item as { index?: unknown }).index;
    const index = Number.isInteger(rawIndex) ? (rawIndex as number) : fallbackIndex;
    const embedding = (item as { embedding?: unknown }).embedding;
    if (
      index < 0 ||
      index >= inputCount ||
      !Array.isArray(embedding) ||
      !embedding.every((value) => typeof value === "number")
    ) {
      return;
    }
    if (embedding.length !== DEFAULT_EMBEDDING_DIMENSION) {
      throw new Error(
        `AI embeddings endpoint returned ${embedding.length}-dimension embedding at index ${index}; expected ${DEFAULT_EMBEDDING_DIMENSION}`,
      );
    }
    embeddings[index] = embedding;
    validCount += 1;
  });

  if (embeddings.some((embedding) => !embedding)) {
    throw new Error(
      `AI embeddings endpoint returned ${validCount} valid embedding(s) for ${inputCount} input(s)`,
    );
  }

  return embeddings as number[][];
}

async function parseEmbeddingResponse(
  response: Response,
  endpoint: string,
  inputCount: number,
): Promise<number[][]> {
  const contentType = response.headers.get("content-type") ?? "";
  const raw = await response.text();
  let json: unknown;

  try {
    json = raw ? JSON.parse(raw) : {};
  } catch {
    throw new Error(
      `AI embeddings endpoint returned non-JSON (${response.status}, ${contentType || "missing content-type"}) from ${endpoint}: ${raw.slice(0, 200)}`,
    );
  }

  if (!response.ok) {
    const message = extractEmbeddingError(json) ?? (raw.slice(0, 200) || response.statusText);
    if (response.status === 429) {
      throw new ProviderRateLimitRetryableError(message, {
        errorCode: response.headers.get("x-gencow-error-code") ?? extractEmbeddingErrorCode(json),
        retryAfterMs: parseRetryAfterHeaderMs(response.headers.get("retry-after")),
        status: response.status,
      });
    }
    throw new Error(`AI embeddings endpoint failed (${response.status}) from ${endpoint}: ${message}`);
  }

  return validateEmbeddingList(json, inputCount);
}

export async function fetchOpenAICompatibleEmbeddings(params: {
  targets: RagEmbeddingTarget[];
  texts: string[];
  fetchImpl?: typeof fetch;
}): Promise<number[][]> {
  if (params.texts.length === 0) return [];
  const fetchImpl = params.fetchImpl ?? fetch;
  let lastError: Error | null = null;

  for (let index = 0; index < params.targets.length; index += 1) {
    const target = params.targets[index];
    let response: Response;
    try {
      response = await fetchImpl(target.endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${target.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: DEFAULT_EMBEDDING_MODEL,
          input: params.texts,
        }),
      });
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (index < params.targets.length - 1) continue;
      throw lastError;
    }

    if (!response.ok && shouldRetryEmbeddingResponse(response.status) && index < params.targets.length - 1) {
      lastError = new Error(
        `AI embeddings endpoint returned retryable status ${response.status} from ${target.endpoint}`,
      );
      continue;
    }

    return parseEmbeddingResponse(response, target.endpoint, params.texts.length);
  }

  throw lastError ?? new Error("AI embeddings endpoint is not configured");
}
